import { default as moment } from "moment";
import React, { useState } from "react";
import striptags from "striptags";
import labelProvider from '../../Catalog.labels';
import { CardView } from "common/views/card/Card.view";
import HeadingView from "common/views/heading/Heading.view";
import Icon from "common/views/icon/Icon";
import { ImageVariant, ImageView } from "common/views/image/Image.view";
import ProgressBarView from "common/views/progressBar/ProgressBar.view";
import { RowsView } from "common/views/rows/Rows.view";
import { TagView } from "common/views/tag/TagView.view";
import TypographyView from "common/views/typographyView/Typography.view";
import { FragmentProvider, IDataFragment } from "core/components/FragmentProvider";
import DateLabel from "core/components/label/DateLabel";
import Label from "core/components/label/Label";
import CatalogLabels from "panels/catalog/Catalog.labels";
import ICourseFragmentProps from "../../fragments/course/ICourseFragmentProps";
import itemTypeOverviewFragments from "../../fragments/course/itemTypeOverviewFragments";
import { ButtonVariant, ButtonView } from "../../../../common/views/button/Button.view";
import HtmlDefault from "../../../../common/fragments/HtmlDefault";
import styled from "styled-components";
import { BoxView } from "common/views/box/Box.view";
import PickLabels from "core/components/label/PickLabels";
import { TextInputVariant, TextInputView } from "common/views/textInput/TextInput.view";
import IconView from "common/views/icon/Icon.view";
import Rating from "react-rating";
import { BlockVariant, BlockView } from "common/views/block/BlockView.view";
import { WrapperDivView } from "common/views/wrapperDiv/WrapperDiv.view";

// title
export const title: IDataFragment<ICourseFragmentProps> = {
	name: "Title",
	label: { k: "course.title", v: "Title" },
	// disabled: ({course}) => course && !course.title, // if the course has loaded, but it does not have a title, hide this
	component: ({ course }) => {
		return (
			<WrapperDivView viewKey="course.title.wrapper">
				<HeadingView viewKey="course.title">
					{course && course.title}
				</HeadingView>
			</WrapperDivView>
		);
	}
};

// subtitle
export const subTitle: IDataFragment<ICourseFragmentProps> = {
	name: "Sub Title",
	label: { k: "course.subTitle", v: "SubTitle" },
	component: ({ course }) => {
		return (
			<HeadingView viewKey="course.subTitle">
				{course && course.subTitle}
			</HeadingView>
		);
	}
};

// description
export const description: IDataFragment<ICourseFragmentProps> = {
	name: "Description (sanitized)",
	label: { k: "course.description", v: "Description" },
	// disabled: ({course}) => course && !course.description, // if the course has loaded, but it does not have a description, hide this
	component: ({ course }) => (
		<TypographyView viewKey="course.description">
			{course && striptags(course.description)}
		</TypographyView>
	)
};

// description
export const descriptionHtml: IDataFragment<ICourseFragmentProps> = {
	name: "Description (html)",
	label: { k: "course.description", v: "Description" },
	// disabled: ({course}) => course && !course.description, // if the course has loaded, but it does not have a description, hide this
	component: ({ course }) => (
		<div className="html-default">
			<TypographyView viewKey="course.description" html={course && course.description} />
		</div>
	)
};

// abstract
export const abstract: IDataFragment<ICourseFragmentProps> = {
	name: "Abstract (sanitized)",
	label: { k: "course.abstract", v: "Abstract" },
	// disabled: ({course}) => course && !course.abstract, // if the course has loaded, but it does not have an abstract, hide this
	component: ({ course }) => (
		<TypographyView viewKey="course.abstract">
			{course && striptags(course.shortDescription)}
		</TypographyView>
	)
};

// abstract
export const abstractHtml: IDataFragment<ICourseFragmentProps> = {
	name: "Abstract (html)",
	label: { k: "course.abstract", v: "Abstract" },
	// disabled: ({course}) => course && !course.abstract, // if the course has loaded, but it does not have an abstract, hide this
	component: ({ course }) => (
		<div className="html-default">
			<TypographyView viewKey="course.abstract" html={course && course.shortDescription} />
		</div>
	)
};

// "new" banner
export const newBanner: IDataFragment<ICourseFragmentProps> = {
	name: "New Banner",
	label: { k: "course.new", v: "New" },
	enabled: ({ course }) => {
		if (course && !course.startDate) {
			return false;
		}

		return moment(new Date()).diff(course.startDate, 'days') < 30;
	},
	component: () => (
		<TypographyView viewKey="course.new">
			<CatalogLabels.Labels.New />
		</TypographyView>
	)
};

// image
export const image: IDataFragment<ICourseFragmentProps> = {
	name: "Static image",
	label: { k: "course.image", v: "Image" },
	component: ({ course }) => (
		<ImageView
			style={{
				display: 'block',
				maxWidth: '100%',
				width: '100%'
			}}
			src={course && course.iconUrl}
			variant={ImageVariant.THUMBNAIL}
			viewKey="course.image.static"
			overlay={course && course.productType && {
				src: course.productType.iconUrl,
				position: "top left"
			}}
		/>
	)
};

// tags
export const tags: IDataFragment<ICourseFragmentProps> = {
	name: "Tags",
	label: { k: "course.tags", v: "Tags" },
	enabled: ({ course }) => course && !!course.tags,
	component: ({ course }) => (
		<div>
			{course.tags.map((tag) => (
				<TagView viewKey="tag" key={tag.id}>{tag.name}</TagView>
			))}
		</div>
	)
};

// export const credits: IDataFragment<ICourseFragmentProps> = {
// 	name: "Credits",
// 	label: { k: "course.credits", v: "Credits" },
// 	enabled: ({ course }) => course && !!course.credits && !!course.credits.length,
// 	component: ({ course }) => {
// 		let CreditVariant = styled.div`display:inline-flex;`;
// 		return (
// 			<div>
// 				<CreditVariant>
// 					{course.credits.map((credit) => (
// 						<BoxView viewKey={`block.credit`}>
// 							<Label
// 								key={credit.id}
// 								k={`credit.${credit.creditType.name}`}
// 								v={"${value} " + credit.creditType.name}
// 								args={{ value: credit.value }}
// 							/>
// 						</BoxView>
// 					))}
// 				</CreditVariant>
// 			</div>
// 		)
// 	}
// };

// export const searchbar: IDataFragment<ICourseFragmentProps> = {
// 	name: "Search",
// 	label: { k: "course.search", v: "Search" },

// 	component: ({ course, handleSearch, active, handleClear }) => {
// 		const [search, setSearchText] = useState(active || '');
// 		const handleChange = (val: string) => {
// 			setSearchText(val);
// 		}
// 		const onSubmit = (e?: any) => {
// 			if (e) {
// 				e.preventDefault();
// 			}
// 			if (search) {
// 				handleSearch(search);
// 			}

// 		}
// 		const onClear = () => {
// 			setSearchText('');
// 			handleClear();
// 		}
// 		return (
// 			course.baseType === "base:program" ? (
// 				<div style={{ display: 'flex' }} onKeyPress={(event) => event.key === "Enter" ? onSubmit() : null}>
// 					<TextInputView
// 						viewKey="searchbar"
// 						variant={TextInputVariant.SEARCH}
// 						value={search}
// 						onChange={handleChange}
// 						style={{ flexGrow: 1 }}
// 					/>
// 					{!!active ? (
// 						<ButtonView onClick={() => onClear()} viewKey="clear">
// 							<Icon icon="cross" />
// 						</ButtonView>
// 					) : (
// 						<ButtonView onClick={() => onSubmit()} viewKey="go">
// 							<Icon icon="search" />
// 						</ButtonView>
// 					)}
// 				</div>) : null
// 		)
// 	}
// };

// // recommended banner

// // abstract
// export const serialized: IDataFragment<ICourseFragmentProps> = {
// 	name: "JSON",
// 	label: { k: "course.JSON", v: "JSON" },
// 	component: ({ course }) => (
// 		<pre className="bp3-code-block">
// 			<code>
// 				{JSON.stringify(course)}
// 			</code>
// 		</pre>
// 	)
// };

// export const displayDateRange: IDataFragment<ICourseFragmentProps> = {
// 	name: "Display Date Range",
// 	label: { k: "course.displayDates", v: "Date" },
// 	component: ({ course }) => (
// 		<TypographyView viewKey="course.displayDates">
// 			{
// 				course && course.displayStartDate && (
// 					<Icon icon="time">
// 						<DateLabel value={course.displayStartDate} endValue={course.displayEndDate} format="lll" />
// 					</Icon>
// 				) || " "
// 			}
// 		</TypographyView>
// 	)
// };

// export const requiredMarker: IDataFragment<ICourseFragmentProps> = {
// 	name: "Required Course Marker",
// 	label: { k: 'course.required', v: 'Required' },
// 	enabled: ({ required }) => required,
// 	component: () => (
// 		<TagView viewKey="course.required">
// 			<CatalogLabels.Labels.Required />
// 		</TagView>
// 	)
// };

// export const overview: IDataFragment<ICourseFragmentProps> = {
// 	name: 'Overview',
// 	label: { k: 'course.overview', v: 'Overview' },
// 	component: (props) => {
// 		if (!props.course || !props.course.baseType) {
// 			return null;
// 		}

// 		// pick fragment based on item type
// 		return (
// 			<FragmentProvider
// 				fragmentList={itemTypeOverviewFragments}
// 				fragments={props.course.baseType}
// 				fragmentProps={props}
// 			/>
// 		);
// 	}
// };

// export const badges: IDataFragment<ICourseFragmentProps> = {
// 	name: 'Badges',
// 	label: { k: 'course.badges', v: 'Badges' },
// 	enabled: (props) => !!(typeof props.renderBadge === 'function' && props.course && props.course.badges && props.course.badges.length),
// 	component: (props) => (
// 		<RowsView viewKey="course.badges.rows">
// 			{props.course.badges.map((badge) => (
// 				<CardView viewKey="course.badge" key={badge.id}>
// 					{props.renderBadge(badge)}
// 				</CardView>
// 			))}
// 		</RowsView>
// 	)
// };

// export const program: IDataFragment<ICourseFragmentProps> = {
// 	name: 'Program',
// 	label: { k: 'course.program', v: 'Program' },
// 	enabled: (props) => !!(typeof props.renderProgram === 'function' && props.course && props.course.program),
// 	component: (props) => (
// 		<CardView viewKey="course.program" key={props.course.program.id}>
// 			{props.renderProgram(props.course.program)}
// 		</CardView>
// 	)
// };

export const price: IDataFragment<ICourseFragmentProps> = {
	name: "Price",
	label: { k: "course.price", v: "Price" },
	enabled: ({ course }) => (course && course.variants && course.variants.length > 0), // skeleton when loading, hide if loaded and not buyable
	component: ({ course, actions }) => (
		course && course.variants.map(v => (
			!v.repurchaseVariant ? <TypographyView viewKey="course.price">
				<CatalogLabels.Labels.CoursePrice args={{ price: v.price, title: v.title }} />
			</TypographyView> : null
		))
	)
};

// export const dueDates: IDataFragment<ICourseFragmentProps> = {
// 	name: "Due Dates",
// 	label: { k: "course.dueDates", v: "Due Dates" },
// 	enabled: ({ course }) => !course || (course && course.dueDates.length > 0), // skeleton when loading, hide if loaded and not present
// 	component: ({ course }) => (
// 		course && course.dueDates.map(v => (
// 			<TypographyView viewKey="course.dueDate">
// 				<CatalogLabels.Labels.CourseDueDate args={{ date: <DateLabel value={v} /> }} />
// 			</TypographyView>
// 		))
// 	)
// };

// export const daysToAccess: IDataFragment<ICourseFragmentProps> = {
// 	name: "Days To Access",
// 	label: { k: "course.subscriptionEndDate", v: "SubscriptionEndDate" },
// 	enabled: ({ course }) => {
// 		if (course && !course.subscriptionEndDate) {
// 			return false;
// 		}
// 		const enddate = moment(course.subscriptionEndDate);
// 		const days = enddate.diff(moment(), 'days');
// 		if (days < 0) {
// 			return false;
// 		}
// 		return true;
// 	},
// 	component: ({ course }) => (
// 		<span>
// 			{moment(course.subscriptionEndDate).diff(moment(new Date()), 'days').toString().concat(' Days Remaining')}
// 		</span>
// 	)
// };

// export const customFields: IDataFragment<ICourseFragmentProps> = {
// 	name: "CustomFields",
// 	label: { k: "course.CustomFields", v: "CustomFields" },

// 	component: ({ course }) => {
// 		const customFields = [course.customField1, course.customField2, course.customField3, course.customField4];
// 		let count = 1;
// 		const component = customFields.map(value => {
// 			if (value)
// 				return (
// 					<TypographyView viewKey={"course.CustomField" + count}>
// 						<span style={{ fontWeight: 'bold', display: 'inline' }}>
// 							<Label k={"course.CustomField" + count} v={"Custom Field " + count++} allowHtml={true} allowMarkdown={true} />
// 						</span>
// 						{value}
// 					</TypographyView>
// 				)
// 			return null;
// 		})

// 		return component;
// 	}
// };

export const averageRating: IDataFragment<ICourseFragmentProps> = {
	name: "Average Rating",
	label: { k: "course.average.rating", v: "Average Rating" },

	component: ({ course }) => {
		return (
			<React.Fragment>
				<BlockView variant={BlockVariant.PAGING} viewKey="avgrating">
					<Label k="course.avgRating" v="Avg. rating: " />
					<ButtonView viewKey="course.avgrating.button" disabled>
						<Rating
							emptySymbol="far fa-star fa-1x medium"
							fullSymbol="fas fa-star fa-1x medium"
							fractions={10}
							initialRating={course && course.avgItemRating || 0}
							readonly
						/>
					</ButtonView>
				</BlockView>
			</React.Fragment>
		)
	}
};

// export const packageItems: IDataFragment<ICourseFragmentProps> = {
// 	name: "Package Items",
// 	label: { k: "course.package.items", v: "Package Items" },

// 	component: ({ course, renderPackageItems }) => {
// 		return (course.baseType === "base:package" && !course.hideItems &&
// 			<React.Fragment>
// 				<HeadingView viewKey="course.items.heading">
// 					<Label k="course.items.heading" v="Courses in package: " />
// 				</HeadingView>
// 				<br></br>
// 				{renderPackageItems(course)}
// 			</React.Fragment>
// 		)
// 	}
// };





// WEBPACK FOOTER //
// ./src/panels/catalog/cart/fragments/cartDisplayFragments.tsx